import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userApi, ListUsersParams } from '../services/userApi';
import { UserCreate, UserUpdate, HardDeleteRequest } from '../types';

export const USERS_QUERY_KEY = 'users';

export function useUsers(params?: ListUsersParams) {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, params],
    queryFn: () => userApi.list(params),
    staleTime: 10_000,
  });
}

export function useUser(id: string, includeGroups = false) {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, id, { includeGroups }],
    queryFn: () => userApi.get(id, includeGroups),
    enabled: !!id,
  });
}

export function useCreateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UserCreate) => userApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useUpdateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UserUpdate }) =>
      userApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useSetUserGroups() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, groupIds }: { id: string; groupIds: string[] }) =>
      userApi.setGroups(id, groupIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useVerifyUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userApi.verify(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useSetUserPassword() {
  return useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      userApi.setPassword(id, password),
  });
}

export function useActivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userApi.activate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useDeactivateUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userApi.deactivate(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useRestoreUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => userApi.restore(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useDeletionImpact(userId: string | null) {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, 'deletion-impact', userId],
    queryFn: () => userApi.getDeletionImpact(userId!),
    enabled: !!userId,
  });
}

export function useHardDeleteUser() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: HardDeleteRequest }) =>
      userApi.hardDelete(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useSetDeletionReassignTarget() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, reassignToUserId }: { id: string; reassignToUserId: string | null }) =>
      userApi.setDeletionReassignTarget(id, reassignToUserId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useBulkDeleteUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => userApi.bulkDelete(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useBulkActivateUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => userApi.bulkActivate(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useBulkDeactivateUsers() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => userApi.bulkDeactivate(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [USERS_QUERY_KEY] });
    },
  });
}

export function useUserActivitySummary(userId: string | undefined) {
  return useQuery({
    queryKey: [USERS_QUERY_KEY, 'activity-summary', userId],
    queryFn: () => userApi.getActivitySummary(userId!),
    enabled: !!userId,
    staleTime: 30_000,
  });
}
